import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { API, usePageTitle, useShop } from "@/context/ShopContext";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

const HERO_URL = "/images/hero.jpg";
const INTRO_URL = "/images/intro.jpg";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.15, delay: 0.25 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.75 + i * 0.13, ease: "easeOut" },
  }),
};

export default function Home() {
  usePageTitle("");
  const { products, meta } = useShop();
  const featured = products.filter((p) => p.featured).slice(0, 4);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    fetch(`${API}/best-sellers`)
      .then((r) => r.json())
      .then((d) => setBestSellers(d.products || []))
      .catch(() => {});
  }, []);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative h-[82vh] md:h-[88vh] min-h-[540px] overflow-hidden" data-testid="hero-section">
        <motion.img
          src={HERO_URL}
          alt="LAMSAH hijabs arranged in signature shades"
          style={{ y: heroY, scale: 1.12 }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-espresso/35" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-8 flex items-end pb-20 md:pb-28">
          <div className="max-w-xl">
            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="text-xs font-semibold uppercase tracking-[0.3em] text-ivory/80 mb-5"
            >
              Every Shade · Your Style
            </motion.p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-ivory leading-[1.08]">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" variants={lineReveal} initial="hidden" animate="show" custom={0}>
                  A Touch of
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" variants={lineReveal} initial="hidden" animate="show" custom={1}>
                  Elegance
                </motion.span>
              </span>
            </h1>
            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="text-sm sm:text-base text-ivory/85 leading-relaxed mt-5 max-w-md"
            >
              Timeless hijabs, thoughtfully chosen for the modern woman.
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="flex flex-wrap gap-4 mt-9">
              <Link to="/shop" data-testid="hero-shop-button" className="btn-primary !bg-ivory !text-espresso hover:!bg-ivory/90">
                Shop Hijabs
              </Link>
              <Link to="/collections" data-testid="hero-collections-button" className="btn-light">
                Explore Collections
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="brand-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <Reveal className="md:col-span-5">
            <p className="overline-label mb-5"><span className="text-rose mr-2">01</span>The LAMSAH Touch</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight leading-snug">
              LAMSAH — <span className="font-arabic" lang="ar" dir="rtl">لمسة</span> — means “a touch.”
            </h2>
            <p className="font-serif text-xl text-rose mt-4 italic">
              A touch of softness. A touch of elegance. A touch that becomes uniquely yours.
            </p>
            <p className="text-sm sm:text-base text-espresso/75 leading-relaxed mt-6">
              Each LAMSAH hijab is carefully selected for its drape, its feel against the
              skin, and the quiet confidence it brings — designed for everyday elegance
              and for the moments that matter most.
            </p>
            <Link to="/about" data-testid="discover-lamsah-button" className="btn-secondary mt-9">
              Discover Lamsah
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full border border-beige hidden md:block" />
              <img
                src={INTRO_URL}
                alt="A neatly folded stack of LAMSAH hijabs in warm neutral shades"
                loading="lazy"
                className="relative w-full aspect-[4/3] object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-28" data-testid="home-collections">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal className="flex items-end justify-between gap-6">
            <div>
              <p className="overline-label mb-4"><span className="text-rose mr-2">02</span>The Collections</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">{(meta.collections || []).length || 21} Collections, {(meta.collections || []).reduce((a, c) => a + (c.count || 0), 0) || 256} Shades</h2>
            </div>
            <Link
              to="/collections"
              data-testid="home-collections-view-all"
              className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-espresso hover:text-rose transition-colors duration-300 shrink-0"
            >
              View All <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
          <div className="mt-12 flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {(meta.collections || []).map((c, i) => (
              <Link
                key={c.slug}
                to={`/collections/${c.slug}`}
                data-testid={`home-collection-${c.slug}`}
                className="group shrink-0 w-[240px] sm:w-[280px] snap-start"
              >
                <div className="relative overflow-hidden aspect-[4/3] bg-beige">
                  <img
                    src={`/images/collections/${c.slug}.jpg`}
                    alt={`The LAMSAH ${c.name} collection`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-espresso/55 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4">
                    <p className="font-serif text-xl text-ivory">{c.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-ivory/80 mt-0.5">{c.fabric} · {c.count} shades</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-28 bg-white" data-testid="shop-by-colour">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <p className="overline-label mb-4"><span className="text-rose mr-2">03</span>Shop by Colour</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">Find Your Shade</h2>
            <p className="text-sm text-espresso/70 mt-3">Nine colour families. Every shade. Your style.</p>
          </Reveal>
          <div className="grid grid-cols-3 gap-x-4 gap-y-10 mt-14 max-w-3xl">
            {meta.families.map((f, i) => (
              <Reveal key={f.name} delay={Math.min(i * 0.05, 0.4)}>
                <Link
                  to={`/colours/${f.slug}`}
                  data-testid={`shade-family-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex flex-col items-center gap-3"
                  aria-label={`Shop the ${f.name} family`}
                >
                  <span
                    className="w-14 h-14 md:w-16 md:h-16 rounded-full border border-beige ring-1 ring-transparent ring-offset-4 ring-offset-white transition-all duration-500 group-hover:ring-taupe group-hover:scale-105"
                    style={{ backgroundColor: f.hex }}
                  />
                  <span className="text-[11px] uppercase tracking-[0.18em] text-taupe group-hover:text-espresso transition-colors duration-300">
                    {f.name}
                  </span>
                  <span className="text-[10px] text-taupe/60 -mt-2">{f.shades.length} shades</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="featured-collection">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal className="flex items-end justify-between gap-6">
            <div>
              <p className="overline-label mb-4"><span className="text-rose mr-2">04</span>Featured Collection</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">The LAMSAH Edit</h2>
              <p className="text-sm text-espresso/70 mt-3 max-w-md">
                A curated selection of timeless shades for every mood and moment.
              </p>
            </div>
            <Link
              to="/shop"
              data-testid="edit-view-all"
              className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-espresso hover:text-rose transition-colors duration-300 shrink-0"
            >
              View All <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12 mt-14">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
          <div className="mt-12 sm:hidden">
            <Link to="/shop" className="btn-secondary w-full">View All Hijabs</Link>
          </div>
        </div>
      </section>

      {bestSellers.length > 0 && (
        <section className="py-24 md:py-32" data-testid="best-sellers-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <Reveal className="flex items-end justify-between gap-6">
              <div>
                <p className="overline-label mb-4"><span className="text-rose mr-2">05</span>Most Loved</p>
                <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">Best Sellers</h2>
                <p className="text-sm text-espresso/70 mt-3 max-w-md">
                  The shades our customers order again and again.
                </p>
              </div>
              <Link
                to="/shop"
                data-testid="best-sellers-view-all"
                className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-espresso hover:text-rose transition-colors duration-300 shrink-0"
              >
                View All <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12 mt-14">
              {bestSellers.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-24 md:py-28 bg-white" data-testid="gift-packaging-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <p className="overline-label mb-4"><span className="text-rose mr-2">06</span>The LAMSAH Unboxing</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight">Every Order Arrives Gift-Ready</h2>
            <p className="text-sm text-espresso/70 mt-3 max-w-md">
              Your hijab, folded in tissue, tied with ribbon and boxed with a hand-written touch — packed with care, made to be gifted.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mt-14">
            {[
              { id: "zareen-shahraz", label: "Zareen · Shahraz" },
              { id: "najwa-yasmin", label: "Najwa · Yasmin" },
              { id: "reem-samaa", label: "Reem · Samaa" },
              { id: "mahra-rumman", label: "Mahra · Rumman" },
            ].map((g, i) => (
              <Reveal key={g.id} delay={i * 0.08}>
                <Link to={`/product/${g.id}`} data-testid={`unboxing-tile-${g.id}`} className="group block">
                  <div className="overflow-hidden bg-beige aspect-square">
                    <img
                      src={`/images/products/packaging-${g.id}.jpg`}
                      alt={`LAMSAH gift packaging — ${g.label}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-taupe mt-3">{g.label}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-9 border-y border-beige overflow-hidden" data-testid="editorial-marquee" aria-hidden="true">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((k) => (
            <div key={k} className="flex shrink-0 items-center">
              {[...Array(6)].map((_, i) => (
                <span key={i} className="mx-10 font-serif text-2xl md:text-3xl font-light text-espresso/45 tracking-wide">
                  Every Shade <span className="text-rose/50 mx-4">·</span> Your Style
                  <span className="text-rose/50 mx-4">·</span>
                  <span className="font-arabic" lang="ar" dir="rtl">لمسة</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-sage py-24 md:py-28" data-testid="editorial-strip">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <p className="font-arabic text-3xl text-espresso/60" lang="ar" dir="rtl">لمسة</p>
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light leading-snug mt-6">
              “Woven for the everyday, worthy of the occasion.”
            </p>
            <Link to="/about" data-testid="editorial-story-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-espresso mt-8 border-b border-espresso/40 pb-1 hover:text-rose hover:border-rose transition-colors duration-300">
              Our Story
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
